// components/NewOrderSelector/components/SubItems/SubItems.tsx

import React, { useState, useMemo } from 'react';
import { SelectionGroup, SelectionGroupItem, getSelectionItemPriceModifier } from '../../../../types';
import { Button, Form } from 'react-bootstrap';
import './SubItems.scss'; // Import the SCSS file
import { formatCurrency } from '../../../../../../../../../../../utils/functions';
import { FaChevronDown, FaChevronUp, FaPlus, FaMinus, FaSearch } from 'react-icons/fa'; // Using react-icons for chevron
import StockIndicator from '../../../../../StockIndicator/StockIndicator';

interface SubItemsProps {
 selectionGroups: SelectionGroup[];
 selectedSelectionGroups: {
   [selectionGroupId: number]: {
     [itemId: number]: number;
   };
 };
 selectionGroupItemComments: {
   [selectionGroupId: number]: {
     [itemId: number]: string;
   };
 };
 selectionGroupItemCourseTags: {
   [selectionGroupId: number]: {
     [itemId: number]: 'ENTRADA' | 'FONDO' | undefined;
   };
 };
 handleSelectionGroupItemChange: (
   sg: SelectionGroup,
   sgItem: SelectionGroupItem,
   increment: boolean
 ) => void;
 handleSelectionGroupItemCommentChange: (
   selectionGroupId: number,
   itemId: number,
   comment: string
 ) => void;
 handleSelectionGroupItemCourseTagToggle: (
   selectionGroupId: number,
   itemId: number
 ) => void;
 pedidoType: "Delivery" | "Caja" | "Restaurant" | "Autoservicio"; // Add pedidoType prop
 getItemStockInfo?: (itemId: number) => {
   hasShortage: boolean;
   hasWarning: boolean;
   shortageInfo?: any;
   warningInfo?: any;
 };
}

const SubItems: React.FC<SubItemsProps> = ({
 selectionGroups,
 selectedSelectionGroups,
 selectionGroupItemComments,
 selectionGroupItemCourseTags,
 handleSelectionGroupItemChange,
 handleSelectionGroupItemCommentChange,
 handleSelectionGroupItemCourseTagToggle,
 pedidoType, // Add to destructured props
 getItemStockInfo,
}) => {

 
 // State to manage collapsed/expanded for each selection group
 const [collapsedGroups, setCollapsedGroups] = useState<{ [sgId: number]: boolean }>({});
 
 // State to manage search terms for each selection group
 const [searchTerms, setSearchTerms] = useState<{ [sgId: number]: string }>({});

 // Sort selection groups: mandatory first (unfolded), optional after (folded by default)
 const sortedSelectionGroups = useMemo(() => {
   return [...selectionGroups].sort((a, b) => {
     const aMandatory = a.min_selections > 0;
     const bMandatory = b.min_selections > 0;
     
     if (aMandatory && !bMandatory) return -1;
     if (!aMandatory && bMandatory) return 1;
     return 0;
   });
 }, [selectionGroups]);

 // Initialize collapsed state based on whether selection group is mandatory
 React.useEffect(() => {
   const initialCollapsedState: { [sgId: number]: boolean } = {};
   sortedSelectionGroups.forEach(sg => {
     const isMandatory = sg.min_selections > 0;
     initialCollapsedState[sg.selection_group_id] = !isMandatory; // Mandatory: false (unfolded), Optional: true (folded)
   });
   setCollapsedGroups(initialCollapsedState);
 }, [sortedSelectionGroups]);

 // Toggle collapsed state
 const toggleCollapse = (sgId: number) => {
   setCollapsedGroups(prev => ({
     ...prev,
     [sgId]: !prev[sgId],
   }));
 };

 // Handle search term change
 const handleSearchChange = (sgId: number, term: string) => {
   setSearchTerms(prev => ({
     ...prev,
     [sgId]: term,
   }));
 };

 // Filter items based on search term
 const getFilteredItems = (sg: SelectionGroup) => {
   const searchTerm = searchTerms[sg.selection_group_id] || '';
   if (!searchTerm) return sg.items;
   
   return sg.items.filter(item =>
     item.item_name.toLowerCase().includes(searchTerm.toLowerCase())
   );
 };

 return (
   <div className="sub-items-container">
     {sortedSelectionGroups.map((sg) => {
       // Defensive validation
       if (!sg || !sg.selection_group_id) {
         console.error('Invalid selection group:', sg);
         return null;
       }
       
       if (!Array.isArray(sg.items)) {
         console.error('Selection group missing items array:', sg);
         return null;
       }
       
       const isSingleSelection =
         sg.min_selections === 1 && sg.max_selections === 1;
       const isOptionalSingleSelection =
         sg.min_selections === 0 && sg.max_selections === 1;
       const isMandatory = (sg.min_selections || 0) > 0;
       const hasSearchBar = sg.items.length > 2; // Set to > 2 for testing, change to > 12 later
       const filteredItems = getFilteredItems(sg);

       return (
         <div key={sg.selection_group_id} className={`selection-group ${isMandatory ? 'mandatory' : 'optional'}`}>
           <div className="selection-group-header" onClick={() => toggleCollapse(sg.selection_group_id)}>
             <div className="header-content">
               <h3 className="selection-group-name">
                 {sg.selection_group_name}
                 {isMandatory && <span className="required-indicator">*</span>}
               </h3>
               <span className="selection-info">
                 {sg.min_selections === sg.max_selections 
                   ? `(${sg.min_selections})`
                   : `(${sg.min_selections}-${sg.max_selections})`
                 }
               </span>
             </div>
             <span className={`chevron-icon ${collapsedGroups[sg.selection_group_id] ? 'collapsed' : 'expanded'}`}>
               {collapsedGroups[sg.selection_group_id] ? <FaChevronDown /> : <FaChevronUp />}
             </span>
           </div>
           
           {!collapsedGroups[sg.selection_group_id] && (
             <div className="selection-group-content">
               <p className="selection-group-description">
                 {sg.selection_group_description}
               </p>
               
               {hasSearchBar && (
                 <div className="search-container">
                   <div className="search-input-wrapper">
                     <FaSearch className="search-icon" />
                     <Form.Control
                       type="text"
                       placeholder={`Buscar en ${sg.selection_group_name}...`}
                       value={searchTerms[sg.selection_group_id] || ''}
                       onChange={(e) => handleSearchChange(sg.selection_group_id, e.target.value)}
                       className="search-input"
                     />
                   </div>
                 </div>
               )}
               
               <div className="selection-group-items">
                 {filteredItems.map((sgItem) => {
                   const currentQty =
                     selectedSelectionGroups[sg.selection_group_id]?.[
                       sgItem.item_id
                     ] || 0;
                   const stockInfo = getItemStockInfo ? getItemStockInfo(sgItem.item_id) : { hasShortage: false, hasWarning: false };

                   if (isSingleSelection || isOptionalSingleSelection) {
                     // Handle single and optional single selections using radio buttons or checkboxes
                     const courseTag = selectionGroupItemCourseTags[sg.selection_group_id]?.[sgItem.item_id];
                     const stockClass = stockInfo.hasShortage ? 'stock-shortage' : stockInfo.hasWarning ? 'stock-warning' : '';
                     
                     return (
                       <div key={sgItem.item_id} className={`selection-item-wrapper ${currentQty > 0 ? 'selected' : ''}`}>
                         <div className={`selection-item ${currentQty > 0 ? 'selected' : ''} ${stockClass}`}>
                           <Form.Check
                             type={isSingleSelection ? 'radio' : 'checkbox'}
                             id={`sg-${sg.selection_group_id}-item-${sgItem.item_id}`}
                             name={`selection-group-${sg.selection_group_id}`}
                             checked={currentQty > 0}
                             onChange={(e) => {
                               if (isSingleSelection) {
                                 // For radio buttons, set quantity to 1 if selected, else 0
                                 handleSelectionGroupItemChange(
                                   sg,
                                   sgItem,
                                   e.target.checked
                                 );
                               } else {
                                 // For checkboxes, toggle quantity between 0 and 1
                                 handleSelectionGroupItemChange(
                                   sg,
                                   sgItem,
                                   e.target.checked
                                 );
                               }
                             }}
                             className="custom-selection-control"
                           />
                           <label 
                             htmlFor={`sg-${sg.selection_group_id}-item-${sgItem.item_id}`}
                             className="selection-item-label"
                             onDoubleClick={() => currentQty > 0 && handleSelectionGroupItemCourseTagToggle(sg.selection_group_id, sgItem.item_id)}
                           >
                             {courseTag && currentQty > 0 && (
                               <span className={`course-tag course-tag--${courseTag.toLowerCase()}`}>
                                 {courseTag}
                               </span>
                             )}
                             <span className="item-name">
                               {sgItem.item_name}
                               <StockIndicator
                                 hasShortage={stockInfo.hasShortage}
                                 hasWarning={stockInfo.hasWarning}
                                 shortageInfo={stockInfo.shortageInfo}
                                 warningInfo={stockInfo.warningInfo}
                                 size="small"
                               />
                             </span>
                             {getSelectionItemPriceModifier(sgItem, pedidoType) !== 0 && (
                               <span className="price-modifier">
                                 +{formatCurrency(getSelectionItemPriceModifier(sgItem, pedidoType))}
                               </span>
                             )}
                           </label>
                         </div>
                         {currentQty > 0 && (
                           <Form.Control
                             type="text"
                             placeholder="Comentario opcional..."
                             value={selectionGroupItemComments[sg.selection_group_id]?.[sgItem.item_id] || ''}
                             onChange={(e) => handleSelectionGroupItemCommentChange(
                               sg.selection_group_id,
                               sgItem.item_id,
                               e.target.value
                             )}
                             className="selection-item-comment"
                           />
                         )}
                       </div>
                     );
                   } else {
                     // Handle multiple selections using -/+ buttons
                     const courseTag = selectionGroupItemCourseTags[sg.selection_group_id]?.[sgItem.item_id];
                     const stockClass = stockInfo.hasShortage ? 'stock-shortage' : stockInfo.hasWarning ? 'stock-warning' : '';
                     
                     return (
                       <div key={sgItem.item_id} className={`selection-item-wrapper ${currentQty > 0 ? 'selected' : ''}`}>
                         <div className={`selection-item multiple ${currentQty > 0 ? 'selected' : ''} ${stockClass}`}>
                           <div 
                             className="item-info"
                             onDoubleClick={() => currentQty > 0 && handleSelectionGroupItemCourseTagToggle(sg.selection_group_id, sgItem.item_id)}
                           >
                             {courseTag && currentQty > 0 && (
                               <span className={`course-tag course-tag--${courseTag.toLowerCase()}`}>
                                 {courseTag}
                               </span>
                             )}
                             <span className="item-name">
                               {sgItem.item_name}
                               <StockIndicator
                                 hasShortage={stockInfo.hasShortage}
                                 hasWarning={stockInfo.hasWarning}
                                 shortageInfo={stockInfo.shortageInfo}
                                 warningInfo={stockInfo.warningInfo}
                                 size="small"
                               />
                             </span>
                             {getSelectionItemPriceModifier(sgItem, pedidoType) !== 0 && (
                               <span className="price-modifier">
                                 +{formatCurrency(getSelectionItemPriceModifier(sgItem, pedidoType))}
                               </span>
                             )}
                           </div>
                           <div className="quantity-controls">
                             <Button
                               variant="outline-secondary"
                               className="quantity-button minus"
                               onClick={() =>
                                 handleSelectionGroupItemChange(sg, sgItem, false)
                               }
                               disabled={currentQty <= 0}
                               aria-label={`Decrement ${sgItem.item_name}`}
                             >
                               <FaMinus />
                             </Button>
                             <span className="quantity-display">{currentQty}</span>
                             <Button
                               variant="outline-secondary"
                               className="quantity-button plus"
                               onClick={(e) => {
                                 e.stopPropagation();
                                 handleSelectionGroupItemChange(sg, sgItem, true);
                               }}
                               aria-label={`Increment ${sgItem.item_name}`}
                               disabled={
                                 currentQty >= sgItem.max_selections ||
                                 Object.values(
                                   selectedSelectionGroups[sg.selection_group_id] || {}
                                 ).reduce((sum, val) => sum + val, 0) >=
                                   sg.max_selections
                               }
                             >
                                <FaPlus />
                             </Button>
                           </div>
                         </div>
                         {currentQty > 0 && (
                           <Form.Control
                             type="text"
                             placeholder="Comentario opcional..."
                             value={selectionGroupItemComments[sg.selection_group_id]?.[sgItem.item_id] || ''}
                             onChange={(e) => handleSelectionGroupItemCommentChange(
                               sg.selection_group_id,
                               sgItem.item_id,
                               e.target.value
                             )}
                             className="selection-item-comment"
                           />
                         )}
                       </div>
                     );
                   }
                 })}
                 
                 {filteredItems.length === 0 && searchTerms[sg.selection_group_id] && (
                   <div className="no-results">
                     No se encontraron elementos que coincidan con "{searchTerms[sg.selection_group_id]}"
                   </div>
                 )}
               </div>
             </div>
           )}
         </div>
       );
     })}
   </div>
 );
};

export default SubItems;
